import { displayInvoiceNumber } from "../../utils/invoiceNumber";
// Column definitions for the invoices grid.
//
// Deliberately JSX-free: keeping this a plain module is what lets the sort
// comparators be unit-tested without rendering anything. `format` returns a
// display string; InvoicesPage special-cases the two rich columns (`number`
// renders as a link, `status` as a pill) by key.
import { money } from "./invoiceStats";

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

// Workflow order, not alphabetical. Alphabetical would sit DRAFT beside FINAL
// and put PAID ahead of SENT, which says nothing about where an invoice is.
export const STATUS_ORDER = { DRAFT: 0, FINAL: 1, SENT: 2, PAID: 3 };

// Display labels for Invoice.billed_from. Exported because the filter dropdown
// in InvoicesPage builds its options from the same source -- two hand-written
// copies of "Lee Humble" is one typo away from a filter that matches nothing.
export const BILLED_FROM_LABELS = {
  LEE: "Lee Humble",
  BRIAN: "Brian Humble",
};

const moneyCol = (key, label) => ({
  key,
  label,
  align: "right",
  sortable: true,
  sortType: "number",
  accessor: (r) => Number(r[key] || 0),
  format: (r) => money(r[key]),
});

export const ALL_COLUMNS = [
  {
    key: "number",
    label: "Invoice ID",
    align: "left",
    sortable: true,
    sortType: "id",
    accessor: (r) => r.number || String(r.id),
    // A draft has no number yet -- "Draft 123" (see utils/invoiceNumber).
    format: (r) => displayInvoiceNumber(r.number) || String(r.id),
  },
  {
    key: "client_name",
    label: "Client Name",
    align: "left",
    sortable: true,
    sortType: "text",
    accessor: (r) => r.client_name || "",
    format: (r) => dash(r.client_name),
  },
  {
    key: "date",
    label: "Date",
    align: "left",
    sortable: true,
    sortType: "date",
    accessor: (r) => r.date || null,
    format: (r) => dash(r.date),
  },
  moneyCol("total", "Amount"),
  {
    key: "due_date",
    label: "Due Date",
    align: "left",
    sortable: true,
    sortType: "date",
    accessor: (r) => r.due_date || null,
    format: (r) => dash(r.due_date),
  },
  {
    key: "status",
    label: "Status",
    align: "center",
    sortable: true,
    sortType: "status",
    accessor: (r) => STATUS_ORDER[(r.status || "DRAFT").toUpperCase()] ?? 0,
    format: (r) => (r.status || "DRAFT"),
  },
  // --- optional, off by default ---
  {
    key: "billed_from",
    label: "Billed From",
    align: "left",
    sortable: true,
    sortType: "text",
    // Both accessor and format return the label: compareBy sorts on the
    // accessor, so returning the enum here would sort by "BRIAN" < "LEE"
    // while the screen shows the names.
    accessor: (r) => BILLED_FROM_LABELS[r.billed_from] || "",
    format: (r) => BILLED_FROM_LABELS[r.billed_from] || "—",
  },
  {
    key: "qbo_doc_number",
    label: "QBO #",
    align: "left",
    sortable: true,
    sortType: "text",
    accessor: (r) => r.qbo_doc_number || "",
    format: (r) => dash(r.qbo_doc_number),
  },
  {
    key: "sync",
    label: "Sync",
    align: "left",
    sortable: true,
    sortType: "text",
    accessor: (r) => (r.do_not_sync ? "locked" : r.sync_state || ""),
    format: (r) =>
      r.do_not_sync
        ? "ClientZone only"
        : dash(r.sync_state),
  },
  moneyCol("subtotal", "Subtotal"),
  moneyCol("gst", "GST"),
  moneyCol("pst", "PST"),
  moneyCol("hst", "HST"),
  moneyCol("disbursements", "Disbursements"),
  moneyCol("discount", "Discount"),
  {
    key: "year_end",
    label: "Year End",
    align: "center",
    sortable: true,
    sortType: "number",
    accessor: (r) =>
      r.year_end === null || r.year_end === undefined || r.year_end === ""
        ? null
        : Number(r.year_end),
    format: (r) => dash(r.year_end),
  },
];

export const DEFAULT_VISIBLE_KEYS = [
  "number",
  "client_name",
  "date",
  "total",
  "due_date",
  "status",
];

export const OPTIONAL_COLUMN_KEYS = ALL_COLUMNS
  .map((c) => c.key)
  .filter((k) => !DEFAULT_VISIBLE_KEYS.includes(k));

export const COLUMNS_STORAGE_KEY = "invoicesColumns";

export function loadVisibleColumns() {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_KEYS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_KEYS;
    // Drop keys from an older build rather than rendering a phantom column.
    const known = new Set(ALL_COLUMNS.map((c) => c.key));
    const valid = parsed.filter((k) => known.has(k));
    return valid.length ? valid : DEFAULT_VISIBLE_KEYS;
  } catch (e) {
    return DEFAULT_VISIBLE_KEYS;
  }
}

export function saveVisibleColumns(keys) {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(keys));
  } catch (e) {
    // Private browsing / quota. The picker still works for this session.
  }
}

export function compareBy(column, direction) {
  const dir = direction === "desc" ? -1 : 1;
  return (a, b) => {
    const av = column.accessor(a);
    const bv = column.accessor(b);

    // Nulls sort last regardless of direction, so flipping the sort never
    // buries the rows the user is actually looking at under blanks.
    const aEmpty = av === null || av === undefined || av === "";
    const bEmpty = bv === null || bv === undefined || bv === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (column.sortType === "text") {
      return dir * String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
    }

    if (column.sortType === "id") {
      const an = Number(av);
      const bn = Number(bv);
      if (Number.isFinite(an) && Number.isFinite(bn) && String(av).trim() !== "" && String(bv).trim() !== "") {
        return dir * (an - bn);
      }
      return dir * String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
    }

    if (column.sortType === "date") {
      return dir * (av < bv ? -1 : av > bv ? 1 : 0);
    }

    return dir * (Number(av) - Number(bv));
  };
}
